import React from 'react';
import { LogOutIcon } from 'lucide-react';
import { Container } from '../../components/ui/Primitives';
import { Seo } from '../../components/Seo';
import { useAcademyHub } from '../../context/AcademyHub';
import { StaffSignIn } from '../../components/academy/admin/StaffSignIn';
import { AdminOverview } from '../../components/academy/admin/AdminOverview';
import { AdminCourses } from '../../components/academy/admin/AdminCourses';
import { AdminForum } from '../../components/academy/admin/AdminForum';
import { AdminMessages } from '../../components/academy/admin/AdminMessages';
import { AdminUsers } from '../../components/academy/admin/AdminUsers';
import { AdminModeration } from '../../components/academy/admin/AdminModeration';
import { AdminSupport } from '../../components/academy/admin/AdminSupport';
import { ROLE_LABELS } from '../../data/academyAdmin';
import type { AcademyRole } from '../../types/academyHub';
type Tab = 'overview' | 'courses' | 'forum' | 'messages' | 'users' | 'moderation' | 'support';
const TABS: {
  id: Tab;
  label: string;
  roles: AcademyRole[];
}[] = [{
  id: 'overview',
  label: 'Overview',
  roles: ['admin', 'instructor']
}, {
  id: 'courses',
  label: 'Courses',
  roles: ['admin', 'instructor']
}, {
  id: 'forum',
  label: 'Forum',
  roles: ['admin', 'instructor', 'moderator']
}, {
  id: 'messages',
  label: 'Messages',
  roles: ['admin', 'instructor']
}, {
  id: 'users',
  label: 'Users',
  roles: ['admin']
}, {
  id: 'moderation',
  label: 'Moderation',
  roles: ['admin', 'moderator']
}, {
  id: 'support',
  label: 'Support chat',
  roles: ['admin', 'moderator']
}];
export function AcademyAdmin() {
  const {
    role,
    staff,
    hub,
    staffSignOut
  } = useAcademyHub();
  const tabs = TABS.filter(t => t.roles.includes(role));
  const [tab, setTab] = React.useState<Tab>('overview');
  const active = tabs.some(t => t.id === tab) ? tab : tabs[0]?.id;

  // Learners never see the admin area, only a staff sign-in.
  if (!staff || role === 'learner' || !active) return <StaffSignIn data-id="element-1474" data-mp="3unwu85xjxxmb" />;
  const badges: Partial<Record<Tab, number>> = {
    moderation: hub.flags.filter(f => f.status === 'pending').length,
    support: hub.chat.length > 0 && hub.chat[hub.chat.length - 1].from === 'learner' ? 1 : 0,
    messages: new Set(hub.messages.filter(m => m.from === 'learner' && !m.read).map(m => m.learnerEmail)).size
  };
  const title = role === 'instructor' ? 'Instructor' : role === 'moderator' ? 'Moderation' : 'Academy admin';
  return <>
      <Seo title={`${title}, Cavalla Academy`} description="Manage the Academy." path="/academy/admin" noIndex data-id="element-1475" data-mp="3llb33j463wsa" />
      <Container className="py-10 sm:py-14" data-id="element-1476" data-mp="3gqorbeu6c27o">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between" data-id="element-1477" data-mp="3buge46zjej35">
          <div data-id="element-1478" data-mp="36v3jv16c69qj">
            <h1 className="font-display text-[28px] leading-tight text-ink sm:text-[34px]" data-id="element-1479" data-mp="36vmxi5oyebyr">{title}</h1>
            <p className="mt-1 text-small text-muted" data-id="element-1480" data-mp="32s755ljx2xrv">
              Signed in as {staff.name}, {ROLE_LABELS[role as keyof typeof ROLE_LABELS]}
            </p>
          </div>
          <button type="button" onClick={staffSignOut} className="inline-flex items-center gap-2 self-start text-small text-royal hover:text-ink sm:self-auto" data-id="element-1481" data-mp="1dceabut0seeh">
            <LogOutIcon className="h-4 w-4" aria-hidden="true" data-id="element-1482" data-mp="1mdcjeribfc3x" />
            Sign out of staff area
          </button>
        </div>

        <div role="tablist" aria-label="Admin sections" className="mt-8 flex gap-6 overflow-x-auto border-b border-line" data-id="element-1483" data-mp="1rakygj86300y">
          {tabs.map(t => {
          const badge = badges[t.id] ?? 0;
          return <button key={t.id} type="button" role="tab" id={`tab-${t.id}`} aria-selected={active === t.id} aria-controls={`panel-${t.id}`} onClick={() => setTab(t.id)} className={`-mb-px flex shrink-0 items-center gap-2 border-b-2 py-3 text-[14px] transition-colors duration-150 ${active === t.id ? 'border-gold text-ink' : 'border-transparent text-body hover:text-ink'}`} data-id="element-1484" data-mp="13ls1qkjq0o5a">
                {t.label}
                {badge > 0 && <span className="rounded-full bg-gold px-1.5 text-[11px] font-semibold text-charcoal" data-id="element-1485" data-mp="2dhykayp4ue0c">{badge}</span>}
              </button>;
        })}
        </div>

        <div role="tabpanel" id={`panel-${active}`} aria-labelledby={`tab-${active}`} className="pt-8" data-id="element-1486" data-mp="2mj6izhvk9guy">
          {active === 'overview' && <AdminOverview data-id="element-1487" data-mp="2wa9i4e4xjlrl" />}
          {active === 'courses' && <AdminCourses data-id="element-1488" data-mp="2w9rdou84618l" />}
          {active === 'forum' && <AdminForum data-id="element-1489" data-mp="3a9sek9odp8o2" />}
          {active === 'messages' && <AdminMessages data-id="element-1490" data-mp="3a9insjviexhb" />}
          {active === 'users' && <AdminUsers data-id="element-1491" data-mp="368qejqmn0sl1" />}
          {active === 'moderation' && <AdminModeration data-id="element-1492" data-mp="3698hf63t251x" />}
          {active === 'support' && <AdminSupport data-id="element-1493" data-mp="1fvhpc03vgchr" />}
        </div>
      </Container>
    </>;
}